import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  HostListener,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { UsersFacade } from '../../store/users/users.facade';
import { UserFilter } from '../../core/models';
import { BaseTableComponent } from '../../shared/components/base-table/base-table.component';
import { FilterChipsBarComponent } from '../../shared/components/filter-chips-bar/filter-chips-bar.component';
import {
  ColumnVisibilityToggleComponent,
  GridColumn,
} from '../../shared/components/column-visibility-toggle/column-visibility-toggle.component';
import {
  UkgTableData,
  transformToTableData,
  nullishFallback,
} from '../../shared/utils/table-data-transformer';
import { UsersRepository } from '../../infrastructure/http/repositories/users.repository';
import { UserFilterMapper } from '../../shared/utils/user-filter-mapper';
import { FilterPersistenceService } from '../../shared/services/filter-persistence.service';
import { FilterPersistenceManager } from '../../shared/utils/filter-persistence-manager';
import { hasActiveFilters } from '../../shared/utils/filter-reset.utility';
import { LoadingStateComponent } from '../../shared/components/loading-state/loading-state.component';
import { ErrorStateComponent } from '../../shared/components/error-state/error-state.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { ColumnSearchRowComponent } from '../../shared/components/column-search-row/column-search-row.component';
import { ResultsInfoComponent } from '../../shared/components/results-info/results-info.component';
import { FilterPanelWrapperComponent } from '../../shared/components/filter-panel-wrapper/filter-panel-wrapper.component';
import { SearchInputComponent } from '../../shared/components/search-input/search-input.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';

@Component({
  selector: 'app-fps-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FilterChipsBarComponent,
    ColumnVisibilityToggleComponent,
    LoadingStateComponent,
    ErrorStateComponent,
    EmptyStateComponent,
    ColumnSearchRowComponent,
    ResultsInfoComponent,
    FilterPanelWrapperComponent,
    SearchInputComponent,
    PaginationComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersComponent extends BaseTableComponent implements OnInit {
  protected override readonly facade = inject(UsersFacade);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly elementRef = inject(ElementRef);
  private readonly usersRepository = inject(UsersRepository);
  private readonly filterPersistence = inject(FilterPersistenceService);
  private readonly filterManager = new FilterPersistenceManager(
    this.route,
    this.router,
    this.filterPersistence,
    UserFilterMapper,
    'usersPageFilters',
    '/users'
  );

  // Expose Math to template
  override Math = Math;

  // State from facade
  readonly users$ = this.facade.users$;
  readonly loading$ = this.facade.loading$;
  readonly error$ = this.facade.error$;
  readonly roles$ = this.facade.roles$;
  readonly currentFilter$ = this.facade.currentFilter$;

  // Filters
  statusFilter: boolean | null = null;
  roleFilter: string | null = '';

  // Export menu state
  exportMenuOpen = false;

  // Dynamic filter options from API
  availableRoles: string[] = [];

  protected readonly columnVisibilityKeyName = 'usersColumnVisibility';

  columns: GridColumn[] = [
    { field: 'id', header: 'User ID', width: '100px' },
    { field: 'firstName', header: 'First Name', width: '140px' },
    { field: 'lastName', header: 'Last Name', width: '140px' },
    { field: 'userName', header: 'Username', width: '140px' },
    { field: 'roleName', header: 'Role', width: '140px' },
    {
      field: 'adGroup',
      header: 'AD Group',
      sortable: true,
      width: '140px',
    },
    { field: 'email', header: 'Email', width: '220px' },
    { field: 'workPhoneNumber', header: 'Work Phone', width: '160px' },
    {
      field: 'active',
      header: 'Status',
      width: '100px',
      type: 'status',
    },
  ].map(col => ({ ...col, sortable: true, visible: true })) as GridColumn[];

  readonly columnWidthMap: Record<string, string> = {
    id: '100px',
    userName: '140px',
    firstName: '140px',
    lastName: '140px',
    email: '220px',
    roleName: '140px',
    adGroup: '140px',
    workPhoneNumber: '160px',
    active: '100px',
  };

  readonly widthRatios: Record<string, number> = {
    id: 90,
    userName: 120,
    firstName: 140,
    lastName: 140,
    email: 260,
    roleName: 140,
    workPhoneNumber: 160,
    active: 100,
  };

  get visibleColumns(): GridColumn[] {
    return this.columnVisibilityService.getVisibleColumns(this.columns);
  }

  get filterRowGridTemplate(): string {
    return this.columnVisibilityService.getFilterRowGridTemplate(
      this.visibleColumns,
      this.widthRatios
    );
  }

  // Transform users data to Ignite table format
  tableData$: Observable<UkgTableData[]> = this.facade.users$.pipe(
    map(pagedResult =>
      transformToTableData(pagedResult, user => ({
        id: user.id.toString(),
        firstName: nullishFallback(user.firstName),
        lastName: nullishFallback(user.lastName),
        userName: nullishFallback(user.userName),
        roleName: nullishFallback(user.roleName),
        email: nullishFallback(user.email),
        adGroup: nullishFallback(user.adGroup),
        workPhoneNumber: nullishFallback(user.workPhoneNumber),
        active: user.active ? 'Active' : 'Inactive',
      }))
    )
  );

  // Sorted table data (column filtering is now applied server-side via loadUsers)
  filteredTableData$: Observable<UkgTableData[]> = this.tableData$.pipe(
    map(data =>
      this.sortTableData(data, this.currentSortField, this.currentSortDirection)
    )
  );

  override toggleColumnVisibility(): void {
    super.toggleColumnVisibility(this.columns);
  }

  override resetColumnVisibility(): void {
    super.resetColumnVisibility(this.columns);
  }

  protected override loadColumnVisibility(): void {
    super.loadColumnVisibility(this.columns);
  }

  ngOnInit(): void {
    this.loadColumnVisibility();
    this.initializeSearch(term => this.facade.search(term));
    this.initializeColumnSearch([
      'id',
      'firstName',
      'lastName',
      'userName',
      'roleName',
      'adGroup',
      'email',
      'workPhoneNumber',
      'active',
    ]);

    this.columnLabelMap = this.createColumnLabelMap(this.columns);

    // Reset to page 1 and reload from the server when column filters change
    this.columnFiltersSubject
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const columnFilters: Partial<UserFilter> = {
          page: 1,
          id: this.columnFilters['id']?.trim() ?? undefined,
          userName: this.columnFilters['userName']?.trim() ?? undefined,
          firstName: this.columnFilters['firstName']?.trim() ?? undefined,
          lastName: this.columnFilters['lastName']?.trim() ?? undefined,
          email: this.columnFilters['email']?.trim() ?? undefined,
          adGroup: this.columnFilters['adGroup']?.trim() ?? undefined,
          workPhoneNumber:
            this.columnFilters['workPhoneNumber']?.trim() ?? undefined,
          roleNameColumn: this.columnFilters['roleName']?.trim() ?? undefined,
          activeColumn: this.columnFilters['active']?.trim() ?? undefined,
        };

        this.facade.loadUsers(columnFilters);
      });

    // Subscribe to filter changes to update sort state
    this.currentFilter$.pipe(takeUntil(this.destroy$)).subscribe(filter => {
      if (filter.sortBy) {
        this.currentSortField = filter.sortBy;
        this.currentSortDirection =
          (filter.sortOrder as 'asc' | 'desc') || null;
      } else {
        this.currentSortField = null;
        this.currentSortDirection = null;
      }
      this.cdr.markForCheck();
    });

    this.users$.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result !== null) {
        this.hasLoadedOnce = true;
      }
    });

    // Subscribe to roles - restore filters AFTER roles are loaded
    this.roles$.pipe(takeUntil(this.destroy$)).subscribe(roles => {
      if (roles && roles.length > 0) {
        this.availableRoles = roles.map(role => role.roleName);
        this.cdr.markForCheck();

        // Only restore once, prevent race conditions
        if (!this.filterManager.isFiltersRestored()) {
          const queryParams = this.route.snapshot.queryParams;
          const restored = this.filterManager.restoreFilters(queryParams, {
            statusFilter: null,
            roleFilter: null,
            searchTerm: '',
          });

          this.statusFilter = restored.statusFilter ?? null;
          this.roleFilter = restored.roleFilter ?? null;
          this.searchTerm = restored.searchTerm ?? '';

          this.filterManager.setFiltersRestored(true);
          this.applyFilters();
        }
      }
    });

    // Monitor query params for same-route navigation with guard
    this.filterManager.monitorQueryParamsChanges(
      this.destroy$,
      restored => {
        this.statusFilter = restored.statusFilter ?? null;
        this.roleFilter = restored.roleFilter ?? null;
        this.searchTerm = restored.searchTerm ?? '';
      },
      {
        statusFilter: this.statusFilter,
        roleFilter: this.roleFilter,
        searchTerm: this.searchTerm,
      }
    );

    this.facade.loadUsers();
    this.facade.loadRoles();
  }

  onStatusFilterChange(value: string | boolean): void {
    if (value === '') {
      this.statusFilter = null;
    } else if (value === true || value === 'true') {
      this.statusFilter = true;
    } else {
      this.statusFilter = false;
    }
    this.applyFilters();
  }

  onRoleFilterChange(value: string | null): void {
    this.roleFilter = value === '' ? null : value;
    this.applyFilters();
  }

  applyFilters(): void {
    this.facade.filterByStatus(this.statusFilter);
    this.facade.filterByRole(this.roleFilter);

    const filters = {
      statusFilter: this.statusFilter,
      roleFilter: this.roleFilter,
      searchTerm: this.searchTerm,
    };

    this.filterManager.updateQueryParams(filters);
    this.filterManager.saveFilters(filters);
    this.facade.loadUsers();
  }

  clearAllFilters(): void {
    this.searchTerm = '';
    this.statusFilter = null;
    this.roleFilter = null;
    this.clearAllColumnFilters();
    this.facade.clearFilters();

    const defaultFilters = {
      statusFilter: this.statusFilter,
      roleFilter: this.roleFilter,
      searchTerm: this.searchTerm,
    };

    this.filterManager.clearFilters(defaultFilters);
    this.facade.loadUsers();
  }

  get hasActiveFilters(): boolean {
    return hasActiveFilters(
      {
        statusFilter: this.statusFilter,
        roleFilter: this.roleFilter,
      },
      this.searchTerm
    );
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.statusFilter !== null) count++;
    if (this.roleFilter !== null) count++;
    return count;
  }

  override onSort(field: string): void {
    this.facade.sort(field);
  }

  onTableSort(sortDetail: { key: string; isAsc: boolean | 'none' }): void {
    this.facade.sort(sortDetail.key);
  }

  getSortIcon(field: string): string {
    if (this.currentSortField !== field || !this.currentSortDirection)
      return '';
    return this.currentSortDirection === 'asc' ? '↑' : '↓';
  }

  getStatusClass(active: boolean | null | undefined): string {
    if (active === null || active === undefined) return 'status-unknown';
    return active ? 'status-active' : 'status-inactive';
  }

  getStatusLabel(active: boolean | null | undefined): string {
    if (active === null || active === undefined) return '-';
    return active ? 'Active' : 'Inactive';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      event.target instanceof HTMLElement &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.exportMenuOpen = false;
    }
  }

  toggleExportMenu(): void {
    this.exportMenuOpen = !this.exportMenuOpen;
  }

  exportAsCSV(): void {
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format (UTC)
    this.callExportAPI('csv', `UserList_FPSCheckPrint_${date}.csv`);
  }

  exportAsHTML(): void {
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format (UTC)
    this.callExportAPI('html', `UserList_FPSCheckPrint_${date}.html`);
  }

  private callExportAPI(format: 'csv' | 'html', filename: string): void {
    const columnFilters = this.columnFilters;

    const filter: UserFilter = {
      search: this.searchTerm ?? undefined,
      status: this.statusFilter ?? undefined,
      roleName: this.roleFilter ?? undefined,
      sortBy: this.currentSortField ?? undefined,
      sortOrder: this.currentSortDirection ?? undefined,
      id: columnFilters['id']?.trim() ?? undefined,
      userName: columnFilters['userName']?.trim() ?? undefined,
      firstName: columnFilters['firstName']?.trim() ?? undefined,
      lastName: columnFilters['lastName']?.trim() ?? undefined,
      email: columnFilters['email']?.trim() ?? undefined,
      adGroup: columnFilters['adGroup']?.trim() ?? undefined,
      workPhoneNumber: columnFilters['workPhoneNumber']?.trim() ?? undefined,
      roleNameColumn: columnFilters['roleName']?.trim() ?? undefined,
      activeColumn: columnFilters['active']?.trim() ?? undefined,
    };

    this.usersRepository
      .exportUsers(format, filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.downloadBlob(blob, filename);
          this.exportMenuOpen = false;
        },
        error: error => {
          console.error('Export failed:', error);
          this.exportMenuOpen = false;
        },
      });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }
}
